import React from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { blockInvalidNumericInput, handleNumericPaste } from '@/utils/validation/helpers';
import { useReminderChipValidation } from '../../user_configuration/user_invoice/invoice-inputs'; // Will be created/updated

interface ReminderChipProps {
  index: number;
  dayInState: number; // The actual numeric value from reminderDays[index]
  allReminderDays: ReadonlyArray<number>;
  paymentTermsDays: number | null;
  onUpdate: (idx: number, newValue: number | null) => void;
  onValidationComplete: (idx: number, isValid: boolean, validatedValue: number | null, originalValueBeforeEdit: number) => void;
  onRemove: (idx: number) => void;
}

export const ReminderChip: React.FC<ReminderChipProps> = ({
  index,
  dayInState,
  allReminderDays,
  paymentTermsDays,
  onUpdate,
  onValidationComplete,
  onRemove,
}) => {
  const validationHook = useReminderChipValidation(
    index,
    dayInState,
    allReminderDays,
    paymentTermsDays,
    onUpdate,
    onValidationComplete
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      (e.currentTarget as HTMLElement).blur();
      return;
    }
    blockInvalidNumericInput(e, { allowDecimal: false, maxValue: paymentTermsDays });
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLSpanElement>) => {
    handleNumericPaste(e, { allowDecimal: false, maxValue: paymentTermsDays });
  };

  const handleBlur = (e: React.FocusEvent<HTMLSpanElement>) => {
    validationHook.handleChipBlur(e.currentTarget.textContent ?? "");
  };

  return (
    <div className="flex flex-col items-start">
      <div className={cn(
        "flex items-center rounded-full bg-muted px-2 py-1 text-sm shadow-sm",
        validationHook.error && "border border-destructive"
      )}>
        <div className="flex">
          <span
            contentEditable
            id={`reminder-chip-${index}`}
            suppressContentEditableWarning
            tabIndex={0}
            data-reminder-idx={index} // For focusing new chips
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBlur={handleBlur}
            className="outline-none min-w-[1ch]"
          >
            {validationHook.inputProps.value === null || validationHook.inputProps.value === 0 ? "" : String(validationHook.inputProps.value)}
          </span>
          <span className="select-none">&nbsp;d</span>
        </div>
        <Button
          size="icon-tiny"
          variant="ghost"
          className="ml-1"
          onClick={() => onRemove(index)}
          aria-label={`Remove ${dayInState === 0 ? 'new' : dayInState}-day reminder`}
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
      {validationHook.errorElement &&
        React.cloneElement(validationHook.errorElement as React.ReactElement, {
          className: cn((validationHook.errorElement as React.ReactElement).props.className, "text-[10px] sm:text-[12px] md:text-xs ml-1")
        })}
    </div>
  );
};
